import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { Col, Container, Row } from "react-bootstrap";
import WelcomeSection from "../../../components/templates/DashboardTeamplate/WelcomeSection/WelcomeSection";
import LoaderHome from "../../../components/atoms/Loader-home/Loader-home";
import { useEffect, useMemo, useState } from "react";
import OperatorPerformanceSummary from "../../../components/molecules/OperatorPerformanceSummary/OperatorPerformanceSummary";
import { ChartOptionsMap, ChartType, LeadGroupCountDto } from "./constants";
import { Link } from "react-router-dom";
import { PeriodView } from "../../../components/templates/DashboardTeamplate/Charts/helpers/chartPeriod";
import RecentLeads from "../../../components/templates/DashboardTeamplate/RecentLeads/RecentLeads";
import { ILeadDto } from "../models/DashboardModels";

interface OperatorPerformanceType {
  id: number;
  profilePictureUrl: string;
  firstName: string;
  lastName: string;
  totalLeads: number;
  hotLeadsCount: number;
  warmLeadsCount: number;
  interestedLeadsCount: number;
  rejectedLeadsCount: number;
  hotWoarmLeadsPercentage: number;
  rejectedLeadsPercentage: number;
  performanceType: number;
}

interface OperatorDetails {
  firstName: string;
  lastName: string;
  totalLeads: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  leadDtos: ILeadDto[];
}

const OperatorDashboardLayout = () => {
  const [data, setData] = useState<OperatorPerformanceType>();
  const [operatorDetails, setOperatorDetails] = useState<OperatorDetails>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { accounts } = useMsal();
  const accessToken = localStorage.getItem("AccessToken");
  const account = accounts[0];
  const { username, idTokenClaims } = account;
  const [activeChart, setActiveChart] = useState<string>("Date");
  const userId = idTokenClaims?.extension_ilpuid;
  const [leadCount, setLeadCount] = useState<any>(null);
  const SelectedChartComponent = ChartOptionsMap[ChartType.Area].component;
  useEffect(() => {
    fetchOPeratorPerformanceDetails();
    fetchOperatorDetails();
    fetchLeads("Date");
  }, []);

  const toView = (activeChart: string): PeriodView => {
    switch (activeChart) {
      case "Month":
        return "month";
      case "Year":
        return "year";
      case "Date":
      default:
        return "date";
    }
  };
  const view = toView(activeChart);
  const chartItems = useMemo(
    () =>
      ["Date", "Month", "Year"].map((item) => ({
        item,
        isActive: item === activeChart,
      })),
    [activeChart]
  );

  const fetchOperatorDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_TODO_LIST_ENDPOINT}/leads/operatorPerformance/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const result = await response.json();
      setOperatorDetails(result);
    } catch (error) {
      setError("Failed to fetch operator performance details.");
    }
  };

  const fetchLeads = async (type: string) => {
    try {
      const accessToken = localStorage.getItem("AccessToken");

      const url = `${
        process.env.REACT_APP_API_TODO_LIST_ENDPOINT
      }/leads/recent/8/${type}${userId ? `?operatorId=${userId}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch leads");
      }

      const data: LeadGroupCountDto[] = await response.json();

      setLeadCount(data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const handleChartClick = (type: string) => {
    setActiveChart(type);
    fetchLeads(type);
  };

  const fetchOPeratorPerformanceDetails = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_TODO_LIST_ENDPOINT}/leads/operatorPerformanceDashboard/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error("Network response was not ok");
      } else if (response.ok) {
        setLoading(false);
        setData(result);
      } else {
        throw new Error(result?.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to fetch operator performance details.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthenticatedTemplate>
      <Container fluid className="layout-bar operator-dashboard dashboard">
        <Row>
          <Col sm={12}>
            <WelcomeSection performance={data?.performanceType} />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            {loading ? (
              <LoaderHome />
            ) : error ? (
              <div className="text-danger fw-semibold">{error}</div>
            ) : data ? (
              <OperatorPerformanceSummary data={data} />
            ) : (
              <div className="text-muted">No performance data available.</div>
            )}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={6}>
            <div>
              {loading ? (
                <div className="loader-wrapper">
                  <LoaderHome />
                </div>
              ) : (
                <div>
                  <RecentLeads
                    allLeads={operatorDetails?.leadDtos?.slice(0, 3) ?? []}
                  />
                </div>
              )}
            </div>
          </Col>
          <Col sm={6}>
            <div>
              <div className="operator-dashboard__chart-section">
                <div className="line-chart-wrapper">
                  <div className="links-wrapper">
                    <div className="title">
                      Lead flow <br />
                      <p>Volume tracking across your acquisition channels</p>
                    </div>
                    <div className="filter-links">
                      {chartItems.map((nav: any, index: number) => (
                        <Link
                          key={index}
                          onClick={() => handleChartClick(nav.item)}
                          className={nav.isActive ? "active" : ""}
                          to={""}
                        >
                          {nav.item}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="line-chart">
                    <SelectedChartComponent leadCount={leadCount} view={view} />
                  </div>
                  <p>
                    All metrics are updated in real-time. Data accuracy is
                    Al-driven!
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </AuthenticatedTemplate>
  );
};

export default OperatorDashboardLayout;
